/**
 * File: web/src/App.tsx
 * =====================
 *
 * Application root (runtime config + global providers + routing)
 *
 * Purpose:
 * - Load runtime configuration from /config.json (generated by the CDK deployment).
 * - Establish top-level UI structure (Header/Footer) and global providers (Toast).
 * - Wire up client-side routes (Upload, Admin, OAuth Callback).
 * - Apply a global auth guard that redirects the user to login when tokens expire.
 *
 * When used:
 * - This is the root component rendered by main.tsx.
 * - It runs once per page load and stays mounted as users navigate routes.
 *
 * Data flow:
 * 1) On mount: loadConfig() fetches /config.json from the same origin (CloudFront → S3).
 * 2) Once config is loaded:
 *    - Header gets config and can build login/logout URLs
 *    - Routes render, passing config down to pages/components that call the API
 *    - useAuthGuard(config) schedules redirect before token expiration (if token exists)
 * 3) If config fails to load: show a configuration error message (no routes).
 *
 * Learner notes:
 * - Runtime config avoids rebuild-per-environment: the same web build artifact can be deployed
 *   to dev/prod because CDK writes config.json at deploy time.
 * - The auth guard is global and "proactive": it redirects before expiry to avoid 401s mid-action.
 *
 * Gotchas:
 * - If config.json is missing, it usually means the CDK site deployment didn't run or web/dist
 *   wasn't present during deploy.
 * - React.StrictMode (if enabled in main.tsx) can run effects twice in development; this code’s
 *   config load is idempotent, but you may see duplicate requests during local dev.
 */
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./styles.css";

import type { AppConfig } from "./lib/types";
import { loadConfig } from "./lib/config";
import { useAuthGuard } from "./lib/useAuthGuard";

import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ToastProvider } from "./components/ToastProvider";

import { UploadRoute } from "./routes/Upload";
import { AdminRoute } from "./routes/Admin";
import { CallbackRoute } from "./routes/Callback";

export function App() {
  /**
   * config:
   * - null means "not loaded yet"
   * - when loaded, holds apiBaseUrl, cognitoDomain, clientId, allowedTypes, etc.
   *
   * Why keep config in state:
   * - Routes/components need it to call the API and build auth URLs.
   * - We don't render protected routes until config exists (prevents undefined values).
   */
  const [config, setConfig] = useState<AppConfig | null>(null);

  /**
   * error:
   * - Non-empty means we failed to load runtime configuration.
   * - In that case we show a simple error UI instead of routes.
   */
  const [error, setError] = useState<string>("");

  useEffect(() => {
    /**
     * Load runtime config once at startup.
     *
     * Why use an async IIFE:
     * - useEffect callbacks cannot be async directly.
     *
     * What loadConfig() does:
     * - fetches /config.json from the deployed site
     * - returns AppConfig (typed contract shared with CDK)
     */
    (async () => {
      try {
        const cfg = await loadConfig();
        setConfig(cfg);
      } catch (e: unknown) {
        // Configuration failures are fatal for the SPA (we don't know API/auth endpoints).
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError(String(e) || "Configuration error");
        }
      }
    })();
  }, []);

  /**
   * Global auth guard:
   * - If a token exists, schedule a redirect shortly before it expires.
   * - If token is already expired, clear it and redirect immediately.
   *
   * Why this is global:
   * - Uploads and admin actions can take time; we want to avoid tokens expiring mid-operation.
   *
   * Note:
   * - The guard does nothing until config is available (it needs config to start login flow).
   */
  useAuthGuard(config);

  /**
   * Config error boundary:
   * - If config fails to load, we can't build routes or auth URLs.
   * - This UI makes the issue visible and actionable.
   */
  if (error) {
    return (
      <div className="container app-shell">
        <div className="notice">
          <strong>Configuration error.</strong> {error}
        </div>
      </div>
    );
  }

  return (
    /**
     * ToastProvider:
     * - Provides the toast "push" API used by routes/components to show transient messages.
     * - Placed at the root so any part of the app can access it.
     */
    <ToastProvider>
      {/**
       * BrowserRouter:
       * - Enables client-side routing.
       * - CloudFront is configured to return index.html for deep links (handled by CDK).
       */}
      <BrowserRouter>
        {/**
         * Header:
         * - Usually contains navigation + Sign in/Sign out controls.
         * - It receives:
         *   - config so it can construct Cognito URLs
         *   - ready flag so it can disable auth actions until config is loaded
         */}
        <Header config={config} ready={!!config} />

        {/**
         * Route rendering is gated on config:
         * - Prevents routes from running without runtime configuration.
         * - Avoids null-checks inside every route/component.
         */}
        {config ? (
          <Routes>
            {/* Upload page: main user workflow */}
            <Route path="/" element={<UploadRoute config={config} />} />

            {/* Admin page: allowlist management (admin-only; enforced in UI + backend) */}
            <Route path="/admin" element={<AdminRoute config={config} />} />

            {/* OAuth callback: completes login and redirects back to the saved destination */}
            <Route
              path="/callback"
              element={<CallbackRoute config={config} />}
            />

            {/**
             * Catch-all:
             * - Redirect unknown routes back to home.
             * - replace prevents polluting browser history with invalid routes.
             */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          /**
           * Loading state:
           * - While config is null, show a friendly loading message.
           * - Keeping this markup inside <main> improves semantics/accessibility.
           */
          <main id="main" className="container app-shell" role="main">
            <div className="notice">Loading…</div>
          </main>
        )}

        {/* Footer: static content / branding / links */}
        <Footer />
      </BrowserRouter>
    </ToastProvider>
  );
}
